// Development page — PDF export content. Rendered off-screen only while exporting, in fixed light
// colours (not the app's theme variables, same as DrillPerformance.js's hidden export table), and
// captured block by block by pdfExport.js's renderReportPdf: every [data-report-block] is one
// unit that never splits across an A4 page. Content = what the page shows: the key-points table
// per position group, then the full sentences per player. Players excluded for the period never
// reach this component (the API leaves them out).
import React from 'react';
import { BLOCKS, POSITION_ORDER, keyPointCompact, stateShort, referenceBadge, fmtDate, PCT_METRICS, matchPct, friendlyLabel } from './developmentShared';

const EX = {
  bg: '#ffffff', ink: '#0f172a', muted: '#64748b', line: '#e2e8f0', head: '#f1f5f9',
  above: '#1d4ed8', below: '#c2410c',
};
const FONT = 'Inter, Arial, sans-serif';
const KINDS = ['specialisation', 'improvement'];
const KIND_LABEL = { specialisation: 'Specialisation', improvement: 'Area to improve' };
const COLS = BLOCKS.flatMap(b => KINDS.map(kind => ({ block: b, kind, label: KIND_LABEL[kind] })));

const block = { padding: '0 0 14px', background: EX.bg };
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 700, color: EX.muted, textTransform: 'uppercase', letterSpacing: 0.3, background: EX.head, borderBottom: `1px solid ${EX.line}` };
const thGroup = { ...th, background: EX.bg, color: EX.ink, fontSize: 12, textTransform: 'none', letterSpacing: 0, borderBottom: `2px solid ${EX.ink}` };
const td = { padding: '7px 8px', fontSize: 13, color: EX.ink, borderBottom: `1px solid ${EX.line}`, verticalAlign: 'top' };
const tdPct = { ...td, padding: '7px 4px', textAlign: 'right', whiteSpace: 'nowrap' };

function Cell({ b, kind }) {
  const kp = b.keyPoints[kind];
  const picked = kp && kp.kind !== 'none';
  const accent = picked ? (kind === 'specialisation' ? EX.above : EX.below) : 'transparent';
  const badge = kind === 'specialisation' ? referenceBadge(b) : null;
  const c = keyPointCompact(kp);
  const badgeEl = badge && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: EX.muted, border: `1px solid ${EX.line}`, borderRadius: 4, padding: '0 4px' }}>{badge.label}</span>;
  return (
    <td style={td}>
      <div style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 7 }}>
        {c ? (
          <>
            <div style={{ fontWeight: 600 }}>{c.main}</div>
            <div style={{ fontSize: 11, color: EX.muted }}>{c.family}{badgeEl}</div>
          </>
        ) : <span style={{ color: EX.muted }}>No standout{badgeEl}</span>}
      </div>
    </td>
  );
}

function PctCell({ b, metricKey }) {
  const v = matchPct(b, metricKey);
  return (
    <td style={{ ...tdPct, color: v.none || v.few ? EX.muted : EX.ink, fontWeight: v.none || v.few ? 400 : 600 }}>
      {v.text}{v.friendly && <sup style={{ fontSize: 9, fontWeight: 700, marginLeft: 1 }}>F</sup>}
      {v.few && <div style={{ fontSize: 10, fontWeight: 400 }}>n={v.n}</div>}
    </td>
  );
}

function GroupTable({ position, players }) {
  return (
    <div data-report-block style={block}>
      <div style={{ fontSize: 12, fontWeight: 700, color: EX.muted, letterSpacing: 0.4, margin: '4px 0 6px' }}>{position}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '16%' }} />
          {COLS.map(c => <col key={`${c.block}-${c.kind}`} style={{ width: '16.5%' }} />)}
          {PCT_METRICS.map(m => <col key={m.key} style={{ width: '6%' }} />)}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...thGroup, borderBottom: 'none' }} />
            <th colSpan={2} style={thGroup}>Training — vs position group</th>
            <th colSpan={2 + PCT_METRICS.length} style={thGroup}>Match — vs position target</th>
          </tr>
          <tr>
            <th style={th}>Player</th>
            {COLS.map(c => <th key={`${c.block}-${c.kind}`} style={th}>{c.label}</th>)}
            {PCT_METRICS.map(m => <th key={m.key} style={{ ...th, padding: '6px 4px', textAlign: 'right' }}>{m.label}%</th>)}
          </tr>
        </thead>
        <tbody>
          {players.map(p => (
            <tr key={p.playerId}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: EX.muted }}>{p.positionGroup}{p.positionGroup !== p.position ? ` (squad ${p.position})` : ''} · {p.minutesInWindow} min</div>
              </td>
              {BLOCKS.map(b => (p[b].keyPoints.state
                ? <td key={b} colSpan={2} style={{ ...td, color: EX.muted }}>— {stateShort(b, p[b])}</td>
                : KINDS.map(kind => <Cell key={`${b}-${kind}`} b={p[b]} kind={kind} />)))}
              {PCT_METRICS.map(m => <PctCell key={m.key} b={p.match} metricKey={m.key} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DevelopmentPdf({ data }) {
  const { meta } = data;
  const players = [...data.players].sort((a, b) => {
    const pa = POSITION_ORDER.indexOf(a.positionGroup), pb = POSITION_ORDER.indexOf(b.positionGroup);
    return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb) || a.name.localeCompare(b.name);
  });
  const groups = [];
  for (const p of players) {
    const g = groups[groups.length - 1];
    if (g && g.position === p.positionGroup) g.players.push(p); else groups.push({ position: p.positionGroup, players: [p] });
  }
  const friendly = friendlyLabel(meta);
  const today = new Date();
  const generated = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;

  return (
    <div style={{ width: 900, padding: 24, background: EX.bg, color: EX.ink, fontFamily: FONT, boxSizing: 'border-box' }}>
      <div data-report-block style={block}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `2px solid ${EX.ink}`, paddingBottom: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Development — {meta.period.label}</div>
          <div style={{ fontSize: 12, color: EX.muted }}>Heracles Almelo · HeraLabX · {generated}</div>
        </div>
        <div style={{ fontSize: 13, marginTop: 8 }}>
          {fmtDate(meta.period.from)} – {fmtDate(meta.period.to)} · {players.length} outfield players
        </div>
        <ul style={{ fontSize: 12, color: EX.ink, margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.55 }}>
          <li><strong>Training</strong>: per scenario (MD+1 → MD, {meta.scenariosIncluded.length} scenarios), training sessions only; the player's total ÷ the squad median of that scenario, averaged, compared with the median of the other players in his position group. A scenario only counts when he missed at most one team session.</li>
          <li><strong>Match</strong>: {meta.matchesIncluded.filter(m => !m.friendly).length} competitive matches{friendly ? ` + the ${friendly}` : ''} of 60+ min; the mean % of his position's match target per match (the same % of target as Match Performance).</li>
          <li>Key point at ±10% vs the group, or 110% / 90% of the target; otherwise "No standout". Training SD is never a key point. A low value is not necessarily a weakness (role, tactics).</li>
        </ul>
        <div style={{ fontSize: 11, color: EX.muted, marginTop: 8 }}>
          <span style={{ borderLeft: `3px solid ${EX.above}`, paddingLeft: 6, marginRight: 14 }}>above group / target</span>
          <span style={{ borderLeft: `3px solid ${EX.below}`, paddingLeft: 6, marginRight: 14 }}>below group / target</span>
          LINE / SQUAD = position group too small, compared with the line / squad · TD% / HID% / SD% = average % of the position match target (grey with n: fewer than 4 matches){friendly ? ` · F = incl. the ${friendly}` : ''}
        </div>
      </div>

      {groups.map(g => <GroupTable key={g.position} position={g.position} players={g.players} />)}

      <div data-report-block data-force-page="true" style={block}>
        <div style={{ fontSize: 18, fontWeight: 800, borderBottom: `2px solid ${EX.ink}`, paddingBottom: 6 }}>Key points per player</div>
      </div>
      {players.map(p => (
        <div key={p.playerId} data-report-block style={{ ...block, paddingBottom: 12 }}>
          <div style={{ borderBottom: `1px solid ${EX.line}`, paddingBottom: 4, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</span>
            <span style={{ fontSize: 12, color: EX.muted }}> · {p.positionGroup} · {p.minutesInWindow} competitive min</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
            {p.sentences.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}
